/**
 * What a countertop's ENDS and EDGES are: its edge profile, how its ends
 * finish, which ends stand open, and the cabinet that holds it up.
 *
 * Two readers ask the same questions and must get the same answers. The 3D
 * slab (`Countertop.jsx`) draws a waterfall leg, a profiled end or a square
 * one from them; the estimate (`estimate-pricing/.../countertopFabrication`)
 * bills the stone and the edges the slab is drawn with. Each used to live
 * inside the renderer, so the estimate could only ever bill a plain rectangle.
 *
 * Long-edge exposure (an island is exposed on both long sides) stays in
 * `countertopExposure.js`; this is the ends. Pure.
 */

const { defaultEndFinish } = require('./countertopExposure');
const { objectsAreTouching } = require('../geometry/objectsTouch');
const { isPeninsulaBackPanel } = require('./peninsulaBack');

/** The object types a countertop can stand on. */
const COUNTERTOP_PARENT_TYPES = ['lower-cabinet', 'upper-cabinet', 'fireplace'];

const EDGE_PROFILES = new Set([
  'eased',
  'beveled',
  'bullnose',
  'ogee',
  'waterfall',
]);
const END_FINISHES = new Set(['match', 'square', 'waterfall']);

/**
 * The top's edge profile. Two write paths feed it — the Edit panel writes the
 * top-level `edgeProfile` (applyObjectFieldUpdate), the kitchen styling tool
 * writes `options.edgeProfile` (styleKitchenObjects) — and the styling layer
 * wins. Anything unknown or missing is `'square'`, the legacy plain slab, so
 * an unstyled countertop draws exactly as it always did.
 */
function countertopEdgeProfile(countertop) {
  const ep = countertop?.options?.edgeProfile ?? countertop?.edgeProfile;
  return EDGE_PROFILES.has(ep) ? ep : 'square';
}

/**
 * How the END edges finish: `'match'` (profiled like the front), `'square'`,
 * or `'waterfall'` (the slab turns down to the floor). Per-object field, the
 * styling layer's `options.endFinish` first; the legacy `edgeProfile:
 * 'waterfall'` means waterfall ends; otherwise `defaultEndFinish` — a
 * free-standing top matches its front, a wall-backed run stays square.
 *
 * @param {Object} countertop
 * @param {{ objects?: Object[], cabinetRuns?: Object[] }} [ctx] see
 *   `isFreestandingCountertop`
 */
function countertopEndFinish(countertop, ctx) {
  const ef = countertop?.options?.endFinish ?? countertop?.endFinish;
  if (END_FINISHES.has(ef)) return ef;
  if (countertopEdgeProfile(countertop) === 'waterfall') return 'waterfall';
  return defaultEndFinish(countertop, ctx);
}

/**
 * The cabinet (or fireplace mantel) a countertop stands on: the first of its
 * `parents` of a countertop-bearing type that resolves to a real object.
 *
 * A run countertop's parents can ALL miss — they list corner-fill coverage
 * items (`cornerFill_*`, never materialized) and the cabinet ids can go stale
 * across a re-solve — so a lower cabinet with a height in the same run stands
 * in. Null when nothing holds the top up.
 */
function countertopSupportCabinet(countertop, objects) {
  const all = objects || [];
  const parent = (countertop?.parents || [])
    .filter((p) => COUNTERTOP_PARENT_TYPES.includes(p?.type))
    .map((p) => all.find((o) => o?.id === p.id))
    .find(Boolean);
  if (parent) return parent;
  if (!countertop?.runId) return null;
  return (
    all.find(
      (o) =>
        o?.runId === countertop.runId &&
        o.category?.['lower-cabinet'] &&
        o.dimensions?.y > 0
    ) ?? null
  );
}

function pointSegDist2(p, a, b) {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const len2 = abx * abx + abz * abz || 1e-9;
  let t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * abx;
  const cz = a.z + t * abz;
  return (p.x - cx) ** 2 + (p.z - cz) ** 2;
}

/** A capping wall is one within 30 cm of the end. */
const CAP_DIST2 = 0.3 ** 2;

/**
 * A wall-backed run countertop's short end is "exposed" (waterfall-able) when no
 * roughly-perpendicular wall caps it — i.e. it's an open end, not a wall
 * corner / adjacent-run junction. The parallel backing wall is ignored.
 * `endPt` is the end's world point; `runDir` the unit along-wall direction.
 */
function isRunEndExposed(endPt, runDir, walls) {
  for (const w of walls || []) {
    if (!w?.center || !w?.dimensions || !w?.rotation) continue;
    const wry = w.rotation.y || 0;
    const wdir = { x: Math.cos(wry), z: -Math.sin(wry) };
    // Only perpendicular-ish walls cap an end; a parallel wall (the backing one)
    // runs alongside the whole top and must not count.
    const parallelism = Math.abs(wdir.x * runDir.x + wdir.z * runDir.z);
    if (parallelism > 0.5) continue;
    const half = (w.dimensions.x || 0) / 2;
    const a = { x: w.center.x - half * wdir.x, z: w.center.z - half * wdir.z };
    const b = { x: w.center.x + half * wdir.x, z: w.center.z + half * wdir.z };
    if (pointSegDist2(endPt, a, b) <= CAP_DIST2) return false; // capped
  }
  return true; // open end
}

// The probe that asks what stands at an end: a thin box just OUTSIDE the end
// face, a centimetre off it and 5 cm deep, inset 3 cm from the top's long
// sides and half the slab thick at the slab's own height. Outside the face, so
// what stands under the top (its cabinets, a dishwasher) or along its side (a
// back panel flush with the end) never reaches it; at counter height, so a
// base cabinet beyond the end — its top the slab's underside — does not either.
const PROBE_GAP_M = 0.01;
const PROBE_DEPTH_M = 0.05;
const PROBE_INSET_M = 0.03;

/**
 * The probe outside one vertical face of a box: `PROBE_GAP_M` off the face
 * and `PROBE_DEPTH_M` deep, inset `PROBE_INSET_M` from the face's two edges
 * (never narrower than half the face), `height` tall centred at `y`. Turned so
 * its local +Z is the face's outward `normal` (three.js Y rotation: local +Z
 * lies at `(sin ry, cos ry)`). A countertop asks it of its two ends
 * (`endProbe`); the estimate asks it of each face of a tub platform
 * (`estimate-pricing/utils/tubPlatformFaces.js`).
 *
 * @param {Object} face
 * @param {{x, z}} face.mid - the face's midpoint on the plan
 * @param {{x, z}} face.normal - its outward unit normal
 * @param {number} face.faceLen - its length along the plan
 * @param {number} face.y - the probe's centre height
 * @param {number} face.height - the probe's height
 */
function faceProbe({ mid, normal, faceLen, y, height }) {
  const out = PROBE_GAP_M + PROBE_DEPTH_M / 2;
  return {
    center: { x: mid.x + out * normal.x, y, z: mid.z + out * normal.z },
    dimensions: {
      x: Math.max(faceLen - 2 * PROBE_INSET_M, faceLen / 2),
      y: height,
      z: PROBE_DEPTH_M,
    },
    rotation: { y: Math.atan2(normal.x, normal.z) },
  };
}

function endProbe(countertop, { dir, sign, half, depth, thick }) {
  const c = countertop.center;
  return faceProbe({
    mid: { x: c.x + sign * half * dir.x, z: c.z + sign * half * dir.z },
    normal: { x: sign * dir.x, z: sign * dir.z },
    faceLen: depth,
    y: c.y,
    height: thick / 2,
  });
}

/**
 * Whether something stands against the end the probe sits at: another top
 * (a seam, a peninsula meeting the run), an appliance, a tall unit — anything
 * of the room's at counter height just past the end face (`objectsAreTouching`,
 * the shared rotated test). `self` — the top, or whatever box asks — never
 * counts.
 */
function isEndStoodAgainst(probe, self, objects) {
  return (objects || []).some(
    (o) =>
      o &&
      o !== self &&
      o.id !== self.id &&
      o.center &&
      o.dimensions &&
      objectsAreTouching(probe, o, 0)
  );
}

/**
 * Which of a countertop's two short ends stand open: `left` is its local -X
 * end, `right` its +X end. An end is open when nothing stands against it: no
 * roughly-perpendicular wall caps it (`isRunEndExposed`) and nothing of the
 * room's stands at counter height just past it (`isEndStoodAgainst`) — the
 * same test for a wall run, an island and a peninsula, whose end meeting the
 * run is closed like any other. A waterfall turns down only at an open end,
 * and the estimate bills legs and end edges by the same answer (user's rule,
 * 2026-10-21; until then a free-standing top was open at both ends and a run
 * end at anything but a wall, so legs were drawn beside ranges and seams). No
 * width or no centre is no ends at all.
 *
 * @param {Object} countertop
 * @param {Object} ctx
 * @param {Object[]} [ctx.objects]      the room's objects
 * @param {Object[]} [ctx.cabinetRuns]  the room's stored runs
 * @param {Object[]} [ctx.walls]        the walls that may cap an end
 * @param {number} [ctx.width]  the top's width when it is not its own
 *   `dimensions.x` (the renderer sizes a legacy top from its cabinet)
 * @param {number} [ctx.depth]  its depth, likewise (`dimensions.z`)
 * @param {number} [ctx.thickness]  its thickness, likewise (`dimensions.y`)
 * @returns {{ left: boolean, right: boolean }}
 */
function exposedEnds(countertop, ctx = {}) {
  const g = endGeometry(countertop, ctx);
  if (!g) return { left: false, right: false };
  const { c, dir, half } = g;
  const open = (sign) => {
    const endPt = {
      x: c.x + sign * half * dir.x,
      z: c.z + sign * half * dir.z,
    };
    if (!isRunEndExposed(endPt, dir, ctx.walls)) return false;
    if (!g.canProbe) return true;
    const probe = endProbe(countertop, { ...g, sign });
    return !isEndStoodAgainst(probe, countertop, ctx.objects);
  };
  return {
    right: open(1), // local +X (halfW) end
    left: open(-1), // local -X (-halfW) end
  };
}

/**
 * What a top's ends are measured from: its centre, its local +X in world, its
 * half length, and whether its depth, thickness and height are known well
 * enough to ask what stands at an end (`canProbe`; without them only a wall
 * can close one). Null for a top with no width or no centre.
 */
function endGeometry(countertop, ctx) {
  const c = countertop?.center;
  const lenX = ctx.width ?? countertop?.dimensions?.x;
  if (!c || !(lenX > 0)) return null;
  const ry = countertop?.rotation?.y || 0;
  const depth = ctx.depth ?? countertop?.dimensions?.z;
  const thick = ctx.thickness ?? countertop?.dimensions?.y;
  return {
    c,
    ry,
    dir: { x: Math.cos(ry), z: -Math.sin(ry) }, // local +X in world
    half: lenX / 2,
    depth,
    thick,
    canProbe: depth > 0 && thick > 0 && Number.isFinite(c.y),
  };
}

// The probe that asks what an end CONTINUES: a box INSIDE the top at that end,
// a leg's thickness (the slab's own) plus the probe gap long, inset from the
// long sides like the end probe, just under the slab — where a leg would turn
// down, and where the back panel of a peninsula the end lies on stands.
const UNDER_PROBE_HEIGHT_M = 0.04;

function underEndProbe(countertop, { c, ry, dir, sign, half, depth, thick }) {
  const along = thick + PROBE_GAP_M;
  const mid = half - along / 2;
  return {
    center: {
      x: c.x + sign * mid * dir.x,
      y: c.y - thick / 2 - PROBE_GAP_M - UNDER_PROBE_HEIGHT_M / 2,
      z: c.z + sign * mid * dir.z,
    },
    dimensions: {
      x: along,
      y: UNDER_PROBE_HEIGHT_M,
      z: Math.max(depth - 2 * PROBE_INSET_M, depth / 2),
    },
    rotation: { y: ry },
  };
}

/**
 * Whether the end the probe sits under lies on a peninsula's BACK line: a
 * peninsula's back panel stands there running ACROSS the top (along the end
 * face, not along the top's own length — its own arm's panel runs beside it).
 */
function endContinuesPeninsulaBack(probe, ry, objects) {
  return (objects || []).some(
    (o) =>
      isPeninsulaBackPanel(o) &&
      o.center &&
      o.dimensions &&
      Math.abs(Math.cos((o.rotation?.y || 0) - ry)) < 0.5 &&
      objectsAreTouching(probe, o, 0)
  );
}

/**
 * Which of a countertop's ends a WATERFALL may turn down at: an open end
 * (`exposedEnds`), but never one that continues a peninsula's finished back.
 *
 * On a floating L the seated arm's slab, and a wall run's last piece, end ON
 * the other arm's back line. A leg there stands a slab of stone in the plane
 * of that back, beside its wood panel — stone, wood, stone along one face
 * (Jerry's House, 2026-10-29; the user's choice: a waterfall at the free ends
 * only). The end is still an open edge — the back edge carried round the
 * corner, polished like it — so `exposedEnds` keeps it; only the leg goes.
 * The renderer draws legs here, the estimate bills them, and the seated top
 * reaches a leg's thickness past its last body only here
 * (`seatedPeninsulaTop.reachOpenEnds`) — so on a back line it does not, and
 * stops level with the other arm's counters.
 *
 * @param {Object} countertop
 * @param {Object} ctx as `exposedEnds`
 * @returns {{ left: boolean, right: boolean }}
 */
function waterfallEnds(countertop, ctx = {}) {
  const ends = exposedEnds(countertop, ctx);
  const g = endGeometry(countertop, ctx);
  if (!g?.canProbe || (!ends.left && !ends.right)) return ends;
  const onBack = (sign) =>
    endContinuesPeninsulaBack(
      underEndProbe(countertop, { ...g, sign }),
      g.ry,
      ctx.objects
    );
  return {
    left: ends.left && !onBack(-1),
    right: ends.right && !onBack(1),
  };
}

module.exports = {
  COUNTERTOP_PARENT_TYPES,
  countertopEdgeProfile,
  countertopEndFinish,
  countertopSupportCabinet,
  exposedEnds,
  faceProbe,
  isEndStoodAgainst,
  isRunEndExposed,
  waterfallEnds,
};
